import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Button } from "./ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from "./ui/command";
import { CaretDown, Check } from "@phosphor-icons/react";
import { http, formatApiError } from "../lib/api";
import { toast } from "sonner";
import TaskAttachments from "./TaskAttachments";

const empty = {
  title: "", description: "", assignee_id: "", priority: "medium",
  due_date: "", project: "", notes: "",
  is_milestone: false, milestone_amount: "",
};

export default function TaskFormDialog({ open, onOpenChange, users, editing, onSaved }) {
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);
  const [projects, setProjects] = useState([]);
  const [projectPickerOpen, setProjectPickerOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    http.get("/projects")
      .then(({ data }) => setProjects(data || []))
      .catch(() => setProjects([]));
  }, [open]);

  useEffect(() => {
    if (editing) {
      setForm({
        title: editing.title || "",
        description: editing.description || "",
        assignee_id: editing.assignee_id || "",
        priority: editing.priority || "medium",
        due_date: editing.due_date ? editing.due_date.slice(0, 10) : "",
        project: editing.project || "",
        notes: editing.notes || "",
        is_milestone: !!editing.is_milestone,
        milestone_amount: editing.milestone_amount ?? "",
      });
    } else {
      setForm(empty);
    }
  }, [editing, open]);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title || !form.assignee_id || !form.project) {
      toast.error("Title, assignee, and project are required");
      return;
    }
    setLoading(true);
    try {
      const payload = {
        ...form,
        due_date: form.due_date || null,
        milestone_amount: form.is_milestone && form.milestone_amount !== ""
          ? parseFloat(form.milestone_amount) : null,
      };
      if (editing) {
        await http.patch(`/tasks/${editing.id}`, payload);
        toast.success("Task updated");
      } else {
        await http.post("/tasks", payload);
        toast.success("Task assigned");
      }
      onSaved();
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  // Admins, PMs and members are all valid assignees. Filter out clients only.
  const members = users.filter(
    (u) => (u.role === "member" || u.role === "manager" || u.role === "admin") || editing?.assignee_id === u.id
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-none border-2 border-zinc-900 shadow-brutal max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="label-overline text-blue-600 mb-1">
            {editing ? "Modify Assignment" : "New Assignment"}
          </div>
          <DialogTitle className="font-display text-2xl font-black tracking-tighter uppercase">
            {editing ? "Edit Task" : "Assign a Task"}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-4 mt-2" data-testid="task-form">
          <Field label="Title">
            <Input
              data-testid="task-title-input"
              value={form.title}
              onChange={(e) => update("title", e.target.value)}
              placeholder="e.g. Draft floorplan revision B"
              className="rounded-none border-zinc-400 focus-visible:ring-blue-600 focus-visible:ring-offset-0"
              required
            />
          </Field>

          <div className="grid grid-cols-2 gap-4">
            <Field label="Project / Job No.">
              <Popover open={projectPickerOpen} onOpenChange={setProjectPickerOpen}>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    data-testid="task-project-select"
                    className="flex h-9 w-full items-center justify-between rounded-none border border-zinc-400 bg-transparent px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-blue-600 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {(() => {
                      const p = projects.find((x) => x.name === form.project);
                      if (!p) return <span className="text-muted-foreground">Select an existing project</span>;
                      return (
                        <span className="truncate">
                          {p.name}{p.title ? ` · ${p.title}` : ""}{p.client_name ? ` · ${p.client_name}` : ""}
                        </span>
                      );
                    })()}
                    <CaretDown size={14} className="opacity-50 shrink-0 ml-2" />
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-[--radix-popover-trigger-width] p-0 rounded-none" align="start">
                  <Command
                    filter={(value, search) => value.includes(search.toLowerCase()) ? 1 : 0}
                  >
                    <CommandInput
                      data-testid="task-project-search"
                      placeholder="Search by Job No., title or client…"
                    />
                    <CommandList>
                      <CommandEmpty>
                        {projects.length === 0
                          ? "No projects yet — create one from the Projects page first."
                          : "No matching projects."}
                      </CommandEmpty>
                      <CommandGroup>
                        {projects.map((p) => (
                          <CommandItem
                            key={p.name}
                            data-testid={`task-project-opt-${p.name}`}
                            value={`${p.name} ${p.title || ""} ${p.client_name || ""}`.toLowerCase()}
                            onSelect={() => {
                              update("project", p.name);
                              setProjectPickerOpen(false);
                            }}
                          >
                            <Check
                              size={14}
                              className={`mr-2 ${form.project === p.name ? "opacity-100 text-emerald-700" : "opacity-0"}`}
                            />
                            <div className="flex-1 min-w-0">
                              <div className="font-mono text-xs">{p.name}</div>
                              {(p.title || p.client_name) && (
                                <div className="text-xs text-zinc-500 truncate">
                                  {p.title}{p.title && p.client_name ? " · " : ""}{p.client_name}
                                </div>
                              )}
                            </div>
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </Field>
            <Field label="Assignee">
              <Select value={form.assignee_id} onValueChange={(v) => update("assignee_id", v)}>
                <SelectTrigger data-testid="task-assignee-select" className="rounded-none border-zinc-400 focus:ring-blue-600 focus:ring-offset-0">
                  <SelectValue placeholder="Select team member" />
                </SelectTrigger>
                <SelectContent className="rounded-none">
                  {members.map((u) => (
                    <SelectItem key={u.id} value={u.id} className="rounded-none">
                      {u.name} · {u.email}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <Field label="Priority">
              <Select value={form.priority} onValueChange={(v) => update("priority", v)}>
                <SelectTrigger data-testid="task-priority-select" className="rounded-none border-zinc-400 focus:ring-blue-600 focus:ring-offset-0">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="rounded-none">
                  <SelectItem value="low" className="rounded-none">Low</SelectItem>
                  <SelectItem value="medium" className="rounded-none">Medium</SelectItem>
                  <SelectItem value="high" className="rounded-none">High</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <Field label="Due Date">
              <Input
                data-testid="task-due-input"
                type="date"
                value={form.due_date}
                onChange={(e) => update("due_date", e.target.value)}
                className="rounded-none border-zinc-400 focus-visible:ring-blue-600 focus-visible:ring-offset-0"
              />
            </Field>
          </div>

          <Field label="Description">
            <Textarea
              data-testid="task-description-input"
              value={form.description}
              onChange={(e) => update("description", e.target.value)}
              placeholder="Scope, deliverables, expectations…"
              className="rounded-none border-zinc-400 focus-visible:ring-blue-600 focus-visible:ring-offset-0 min-h-[90px]"
            />
          </Field>

          <Field label="Notes / Attachments">
            <Textarea
              data-testid="task-notes-input"
              value={form.notes}
              onChange={(e) => update("notes", e.target.value)}
              placeholder="Links, references, files… (internal — not shown to clients)"
              className="rounded-none border-zinc-400 focus-visible:ring-blue-600 focus-visible:ring-offset-0 min-h-[70px]"
            />
          </Field>

          {editing && (
            <div className="border border-zinc-200 bg-zinc-50 p-4">
              <div className="label-overline text-zinc-700 mb-1">Reference Files</div>
              <div className="text-xs text-zinc-500 mb-2">
                Attach up to 5 references (PDF, images, DWG) — visible to studio + the assigned team member.
                Max 5MB (images/PDFs) or 50MB (other files).
              </div>
              <TaskAttachments
                taskId={editing.id}
                files={editing.references}
                canEdit
                canDownload
                onChanged={onSaved}
              />
            </div>
          )}
          {!editing && (
            <div className="border border-dashed border-zinc-300 p-3 text-xs text-zinc-500">
              💡 Save the task first, then reopen it to attach reference files (drawings, PDFs, images).
            </div>
          )}

          {/* Billing milestone */}
          <div className="border border-dashed border-orange-400 bg-orange-50/50 p-4">
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                data-testid="task-milestone-checkbox"
                checked={form.is_milestone}
                onChange={(e) => update("is_milestone", e.target.checked)}
                className="mt-1 accent-orange-600 w-4 h-4"
              />
              <div className="flex-1">
                <div className="label-overline text-orange-700">Billing Milestone</div>
                <div className="text-xs text-zinc-600 mt-1">
                  Mark this task as a client-visible billing milestone with an invoice amount.
                </div>
              </div>
            </label>
            {form.is_milestone && (
              <div className="mt-3 pl-7">
                <label className="label-overline block mb-2 text-zinc-700">Milestone Amount (INR)</label>
                <Input
                  data-testid="task-milestone-amount-input"
                  type="number" min="0" step="1"
                  value={form.milestone_amount}
                  onChange={(e) => update("milestone_amount", e.target.value)}
                  placeholder="e.g. 25000"
                  className="rounded-none border-zinc-400 focus-visible:ring-orange-600 focus-visible:ring-offset-0 max-w-xs"
                />
              </div>
            )}
          </div>

          <div className="flex items-center justify-end gap-3 pt-2 border-t border-zinc-300">
            <Button
              data-testid="task-cancel-button"
              type="button" variant="outline"
              onClick={() => onOpenChange(false)}
              className="rounded-none border-zinc-400 font-mono uppercase tracking-widest text-xs hover:bg-zinc-100"
            >
              Cancel
            </Button>
            <Button
              data-testid="task-submit-button"
              type="submit" disabled={loading}
              className="rounded-none bg-zinc-900 hover:bg-blue-600 text-white font-mono uppercase tracking-widest text-xs"
            >
              {loading ? "Saving…" : editing ? "Save Changes" : "Assign Task"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="label-overline block mb-2 text-zinc-700">{label}</label>
      {children}
    </div>
  );
}
